import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { SITE } from "@/lib/site";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative pt-28 pb-16 md:pt-40 md:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#eef3fb] via-background to-background" />
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.p
            variants={fade} initial="hidden" animate="show" custom={0}
            className="overline text-primary mb-5"
          >
            Est. {SITE.founded} — Grand Rapids, Michigan
          </motion.p>
          <motion.h1
            variants={fade} initial="hidden" animate="show" custom={1}
            className="font-serif text-5xl sm:text-6xl lg:text-[4.5rem] leading-[1.02] tracking-tight text-slate-900"
          >
            Private wealth,
            <br />
            <span className="italic text-primary">stewarded</span> with intention.
          </motion.h1>
          <motion.p
            variants={fade} initial="hidden" animate="show" custom={2}
            className="mt-7 max-w-xl text-lg text-slate-600 leading-relaxed"
          >
            Sagos is a boutique financial consulting and advisory firm helping families
            and founders build, protect and transfer wealth with clarity and discipline.
          </motion.p>

          <motion.div
            variants={fade} initial="hidden" animate="show" custom={3}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-consult-btn"
              onClick={() => navigate("/register")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#0b2a5b] transition-colors duration-200"
            >
              Schedule a Consultation
              <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-full border border-slate-300 text-slate-800 font-medium hover:border-primary hover:text-primary transition-colors duration-200"
            >
              Explore Services
            </a>
          </motion.div>

          <motion.div
            variants={fade} initial="hidden" animate="show" custom={4}
            className="mt-10 flex items-center gap-2 text-sm text-slate-500"
          >
            <ShieldCheck size={17} className="text-primary" />
            Fiduciary advisory — your interests, always first.
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-[0_30px_80px_-30px_rgba(11,42,91,0.45)]">
            <img
              src={SITE.images.hero}
              alt="Sagos advisory consultation"
              className="w-full h-[380px] md:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b2a5b]/60 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-6 -left-4 md:-left-10 bg-white rounded-2xl border border-border shadow-xl px-6 py-5">
            <p className="overline text-slate-400">Assets Advised</p>
            <p className="font-serif text-3xl md:text-4xl text-slate-900 mt-1">$480M+</p>
          </div>
          <div className="absolute -top-5 right-4 bg-[#0b2a5b] text-white rounded-2xl shadow-xl px-6 py-4">
            <p className="overline text-white/60">Client Retention</p>
            <p className="font-serif text-3xl mt-1">97%</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
